import { activity as defaultActivity } from './activity'
import type { SwApi } from './sw'

/**
 * Safe update orchestration (SPEC §12).
 *
 * New build detected -> if nothing is being edited/uploaded: save drafts -> (one tab) update the service
 * worker and activate the waiting worker -> reload ONCE. If a form is dirty or an upload is active: show the
 * VersionNotice and apply automatically when the blocking condition clears (or when the person chooses).
 * Reload loops are impossible: a per-target flag in sessionStorage is set before reloading; if the build
 * is still old afterwards the manager only offers a manual "Update" button.
 */

export type UpdateReason = 'build' | 'contract' | 'chunk'
export type Blocker = 'form' | 'upload' | null

export interface UpdateState {
  phase: 'idle' | 'available' | 'applying' | 'save_failed'
  target: string | null
  reason: UpdateReason | null
  blocker: Blocker
  /** An automatic reload for this target already happened and the build is still old: manual only. */
  manualOnly: boolean
  /** A route chunk failed to load and there is no newer build: offer retry. */
  chunkError: boolean
}

export type ChannelMessage =
  | { type: 'new-build'; target: string; reason: UpdateReason; from: string }
  | { type: 'claim'; target: string; from: string }
  | { type: 'reloading'; target: string; from: string }

export interface ChannelLike {
  postMessage(message: ChannelMessage): void
  addEventListener(type: 'message', listener: (event: MessageEvent<ChannelMessage>) => void): void
  removeEventListener(type: 'message', listener: (event: MessageEvent<ChannelMessage>) => void): void
  close(): void
}

export interface UpdateDeps {
  currentBuild: string
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  channel: ChannelLike | null
  activity?: Pick<typeof defaultActivity, 'blocker' | 'flushDrafts' | 'subscribe'>
  sw: SwApi | null
  reload: () => void
  tabId?: string
  /** How long tabs collect leadership claims. */
  claimWindowMs?: number
  /** Wait after a blocker clears before auto-applying (avoids reloading mid-navigation). */
  settleMs?: number
  swTimeoutMs?: number
  now?: () => number
}

export const initialUpdateState: UpdateState = {
  phase: 'idle',
  target: null,
  reason: null,
  blocker: null,
  manualOnly: false,
  chunkError: false,
}

export type Decision = 'apply' | 'wait' | 'manual'

/** Pure rule: what to do about an available update. */
export function decideUpdate(input: { blocker: Blocker; manualOnly: boolean }): Decision {
  if (input.manualOnly) return 'manual'
  return input.blocker ? 'wait' : 'apply'
}

export const reloadFlagKey = (target: string) => `lumora.pwa.reloaded:${target}`
export const CHUNK_RELOAD_KEY = 'lumora.pwa.chunk-reload'
export const CHUNK_RELOAD_WINDOW_MS = 60_000

const randomId = () => Math.random().toString(36).slice(2) + Date.now().toString(36)

export function createUpdateManager(deps: UpdateDeps) {
  const {
    currentBuild,
    storage,
    channel,
    sw,
    reload,
    claimWindowMs = 150,
    settleMs = 1000,
    swTimeoutMs = 10_000,
    now = Date.now,
  } = deps
  const act = deps.activity ?? defaultActivity
  const tabId = deps.tabId ?? randomId()
  let state: UpdateState = initialUpdateState
  const listeners = new Set<() => void>()
  let settleTimer: ReturnType<typeof setTimeout> | null = null
  const claims = new Map<string, Set<string>>() // target -> tab ids that claimed leadership

  const safeGet = (key: string) => {
    try {
      return storage.getItem(key)
    } catch {
      return null
    }
  }
  const safeSet = (key: string, value: string) => {
    try {
      storage.setItem(key, value)
    } catch {
      /* private mode: the in-memory phase guard still prevents loops within this page lifetime */
    }
  }

  const set = (patch: Partial<UpdateState>) => {
    state = { ...state, ...patch }
    listeners.forEach((l) => l())
  }

  const reloadedFor = (target: string) => safeGet(reloadFlagKey(target)) !== null
  const memoryReloaded = new Set<string>()

  function post(message: ChannelMessage) {
    try {
      channel?.postMessage(message)
    } catch {
      /* closed channel */
    }
  }

  function clearSettle() {
    if (settleTimer) clearTimeout(settleTimer)
    settleTimer = null
  }

  // First evaluation applies right away; later ones (after a blocker cleared) wait for the settle window.
  let sawBlocker = false

  function evaluate() {
    if (state.phase !== 'available' || !state.target) return
    const blocker = act.blocker()
    if (blocker) sawBlocker = true
    if (blocker !== state.blocker) set({ blocker })
    clearSettle()
    if (decideUpdate({ blocker, manualOnly: state.manualOnly }) !== 'apply') return
    settleTimer = setTimeout(() => {
      // Re-check: the person may have started typing again during the settle window.
      if (state.phase === 'available' && decideUpdate({ blocker: act.blocker(), manualOnly: state.manualOnly }) === 'apply') void apply('auto')
      else evaluate()
    }, sawBlocker ? settleMs : 0)
  }

  function onNewBuild(target: string, reason: UpdateReason, fromPeer = false) {
    if (reason !== 'contract' && target === currentBuild) return
    if (state.phase === 'applying') return
    const manualOnly = reloadedFor(target) || memoryReloaded.has(target)
    if (state.target !== target || state.phase === 'idle') {
      set({ phase: 'available', target, reason, manualOnly, chunkError: false })
    }
    if (!fromPeer) post({ type: 'new-build', target, reason, from: tabId })
    evaluate()
  }

  /** Elects one tab (lowest id among claimants) to touch the service worker for this target. */
  async function electLeader(target: string): Promise<boolean> {
    if (!channel) return true
    const set_ = claims.get(target) ?? new Set<string>()
    set_.add(tabId)
    claims.set(target, set_)
    post({ type: 'claim', target, from: tabId })
    await new Promise((r) => setTimeout(r, claimWindowMs))
    const ids = [...(claims.get(target) ?? [])].sort()
    return ids[0] === tabId
  }

  async function apply(mode: 'auto' | 'user', force = false): Promise<void> {
    const target = state.target
    if (!target || state.phase === 'applying') return
    clearSettle()
    set({ phase: 'applying' })
    const saved = await act.flushDrafts()
    if (!saved && !force) {
      // Never claim the draft is safe. The person decides (auto mode can only get here via a race).
      set({ phase: 'save_failed' })
      return
    }
    if (mode === 'auto' && (reloadedFor(target) || memoryReloaded.has(target))) {
      set({ phase: 'available', manualOnly: true })
      return
    }
    const leader = mode === 'user' ? true : await electLeader(target)
    if (leader && sw) {
      await sw.update()
      await sw.activateWaiting(swTimeoutMs)
    } else if (!leader && sw) {
      // A peer drives the worker swap; wait briefly for it, then reload anyway (index.html is network-first).
      await new Promise((r) => setTimeout(r, Math.min(swTimeoutMs, 3000)))
    }
    memoryReloaded.add(target)
    safeSet(reloadFlagKey(target), String(now()))
    post({ type: 'reloading', target, from: tabId })
    reload()
  }

  function onMessage(event: MessageEvent<ChannelMessage>) {
    const m = event.data
    if (!m || m.from === tabId) return
    if (m.type === 'new-build') onNewBuild(m.target, m.reason, true)
    else if (m.type === 'claim') {
      const ids = claims.get(m.target) ?? new Set<string>()
      ids.add(m.from)
      claims.set(m.target, ids)
    }
  }
  channel?.addEventListener('message', onMessage)

  const unsubscribeActivity = act.subscribe(() => {
    if (state.phase === 'available') evaluate()
  })

  return {
    getState: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },
    onNewBuild,
    /** The "Atualizar agora" button (drafts are saved first; `force` after a failed save). */
    applyNow: (force = false) => apply('user', force),
    dismissSaveFailure: () => set({ phase: 'available' }),
    setChunkError: (chunkError: boolean) => set({ chunkError }),
    destroy() {
      clearSettle()
      unsubscribeActivity()
      channel?.removeEventListener('message', onMessage)
      channel?.close()
    },
    tabId,
  }
}

export type UpdateManager = ReturnType<typeof createUpdateManager>
